// src/components/common/InputField.js
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

const InputField = ({
  name,
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  required = false,
  icon: Icon,
  className = '',
  autoComplete,
  prefix,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;
  const hasError = !!error;

  const handleFocus = (e) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label 
          htmlFor={name} 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {Icon && !prefix && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Icon className={`h-5 w-5 transition-colors duration-200 ${
              hasError
                ? 'text-red-400'
                : isFocused
                  ? 'text-primary-500'
                  : 'text-gray-400'
            }`} />
          </div>
        )}

        {prefix && (
          <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-sm font-medium ${
            hasError ? 'text-red-400' : 'text-gray-500 dark:text-gray-400'
          }`}>
            {prefix}
            <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 ml-3" />
          </div>
        )}

        <input
          id={name}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`w-full ${prefix ? 'pl-16' : Icon ? 'pl-12' : 'pl-4'} ${
            isPassword ? 'pr-12' : 'pr-4'
          } py-3 bg-white dark:bg-gray-800 border rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 ${
            hasError
              ? 'border-red-300 focus:border-red-500 focus:ring-red-200 dark:border-red-600 dark:focus:ring-red-900/50'
              : 'border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-primary-200 dark:focus:ring-primary-900/50'
          } placeholder-gray-500 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed`}
          {...props}
        />
        
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            disabled={disabled}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        )}
      </div>
      
      {hasError && (
        <div className="flex items-center space-x-2 text-red-600 dark:text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default InputField;